import {isMobilePhone, validate} from 'class-validator';
import {ILocale} from 'data/country';
import {
  nameDTO,
  numberPasswordDTO,
  passwordDTO,
  siginDTO,
} from 'utils/validates';

/**
 * 전화번호 검증 함수
 * 전화번호가 유효한지 검증합니다.
 * @param {string} phoneNumber - 전화번호
 * @param {ILocale} locale - 로케일
 * @returns {boolean} 유효하면 true, 그렇지 않으면 false
 */
export const isValidatePhoneNumber = (phoneNumber: string, locale: ILocale) => {
  const mobilePhoneValidate = isMobilePhone(phoneNumber, locale);
  const isStartedZero =
    locale === 'ko-KR' ? phoneNumber.replace(/ /gi, '')[0] === '0' : true;
  return mobilePhoneValidate && isStartedZero;
};

/**
 * 회원가입 정보 타입
 * 회원가입에 필요한 정보를 정의합니다.
 */
export type IRegisterInfo = {
  [key: string]: any;
  phone: string;
  verifiCode: string;
  password: string;
  checkPassword: string;
  name: string;
  birthDay: string;
  gender: string;
  nationality: string;
  service: boolean; // 서비스 약관
  privacy: boolean; // 개인정보 약관
  location: boolean; // 위치기반 약관
};

/**
 * 비밀번호 에러 메시지 인터페이스
 * 비밀번호 관련 에러 메시지를 정의합니다.
 * @property {string} password - 비밀번호 에러 메시지
 * @property {string} checkPassword - 비밀번호 확인 에러 메시지
 */
export interface IPasswordErrorMessage {
  password: string;
  checkPassword: string;
}

/**
 * 비밀번호 검증 인터페이스
 * 비밀번호 검증에 필요한 파라미터를 정의합니다.
 * @property {IRegisterInfo} registerInfo - 회원가입 정보
 */
export interface IValidatePassword {
  registerInfo: IRegisterInfo;
}

/**
 * 비밀번호 검증 함수
 * 강력한 비밀번호인지 검증합니다.
 * @param {IValidatePassword} params - 비밀번호 검증 파라미터
 * @returns {Promise<string>} 에러가 없으면 빈 문자열, 있으면 'errorPassword'
 */
export const validatePassword = async ({registerInfo}: IValidatePassword) => {
  const input = new passwordDTO();
  input.password = registerInfo.password;
  const errors = await validate(input);
  return errors.length === 0 ? '' : 'errorPassword';
};

/**
 * 비밀번호 확인 검증 함수
 * 비밀번호와 비밀번호 확인이 일치하는지 검증합니다.
 * @param {IValidatePassword} params - 비밀번호 검증 파라미터
 * @returns {Promise<string>} 일치하면 빈 문자열, 일치하지 않으면 'notMatchPassword'
 */
export const validateCheckPassword = async ({
  registerInfo,
}: IValidatePassword) => {
  return !registerInfo.password ||
    registerInfo.password !== registerInfo.checkPassword
    ? 'notMatchPassword'
    : '';
};

/**
 * 비밀번호 에러 텍스트 인터페이스
 * 비밀번호 관련 에러 텍스트를 정의합니다.
 * @property {string} password - 비밀번호 에러 텍스트
 * @property {string} checkPassword - 비밀번호 확인 에러 텍스트
 */
export interface IErrorTextPassword {
  password: string;
  checkPassword: string;
}

/**
 * 비밀번호 값 검증 인터페이스
 * 비밀번호 값 검증에 필요한 파라미터를 정의합니다.
 * @property {IRegisterInfo} values - 회원가입 정보
 * @property {React.Dispatch<React.SetStateAction<IErrorTextPassword>>} setErrorTexts - 에러 텍스트 설정 함수
 */
export interface IValidateValuePassword {
  values: IRegisterInfo;
  setErrorTexts: React.Dispatch<React.SetStateAction<IErrorTextPassword>>;
}

/**
 * 비밀번호 검증 + 비밀번호 확인 검증 함수
 * 강력한 비밀번호 또는 6자리 숫자 비밀번호를 검증하고, 비밀번호 확인도 검증합니다.
 * @param {IValidateValuePassword} params - 비밀번호 값 검증 파라미터
 * @returns {Promise<string>} 에러가 없으면 빈 문자열, 있으면 에러 메시지
 */
export const validateValuePassword = async ({
  values,
  setErrorTexts,
}: IValidateValuePassword) => {
  const valueKeys = Object.keys(values);
  let error = '';

  for (const key of valueKeys) {
    if (key === 'password') {
      if (values[key]) {
        const input = new passwordDTO();
        input.password = values.password;
        const errors = await validate(input);

        const numberPasswordInput = new numberPasswordDTO();
        numberPasswordInput.password = values.password;
        const numberError = await validate(numberPasswordInput);

        const validated = errors.length === 0 || numberError.length === 0;

        setErrorTexts((prev: IErrorTextPassword) => ({
          ...prev,
          password: validated ? '' : 'errorPassword',
        }));
        error = validated ? '' : 'errorPassword';
      }
    } else if (key === 'checkPassword') {
      if (values.password && values.checkPassword) {
        setErrorTexts((prev: IErrorTextPassword) => ({
          ...prev,
          checkPassword:
            !values.password || values.password !== values.checkPassword
              ? 'notMatchPassword'
              : '',
        }));
        error =
          !values.password || values.password !== values.checkPassword
            ? 'notMatchPassword'
            : '';
      }
    }
  }
  return error;
};

/**
 * 추가 정보 에러 텍스트 인터페이스
 * 추가 정보 관련 에러 텍스트를 정의합니다.
 * @property {string} name - 이름 에러 텍스트
 * @property {string} birthDay - 생년월일 에러 텍스트
 * @property {string} gender - 성별 에러 텍스트
 * @property {string} nationality - 국적 에러 텍스트
 */
export interface IErrorTextExtraInfo {
  name: string;
  birthDay: string;
  gender: string;
  nationality: string;
}

/**
 * 추가 정보 값 검증 인터페이스
 * 추가 정보 값 검증에 필요한 파라미터를 정의합니다.
 * @property {IRegisterInfo} values - 회원가입 정보
 * @property {React.Dispatch<React.SetStateAction<IErrorTextExtraInfo>>} setErrorTexts - 에러 텍스트 설정 함수
 */
export interface IValidateValueExtraInfo {
  values: IRegisterInfo;
  setErrorTexts: React.Dispatch<React.SetStateAction<IErrorTextExtraInfo>>;
}

/**
 * 추가 정보 검증 함수
 * 회원가입 추가 정보를 검증합니다.
 * @param {IValidateValueExtraInfo} params - 추가 정보 값 검증 파라미터
 * @returns {Promise<string>} 에러가 없으면 빈 문자열, 있으면 에러 메시지
 */
export const validateValueExtraInfo = async ({
  values,
  setErrorTexts,
}: IValidateValueExtraInfo) => {
  const valueKeys = Object.keys(values);

  for (const key of valueKeys) {
    if (key === 'name') {
      const input = new nameDTO();
      input.name = values.name;
      const errors = await validate(input);
      if (values.name !== '') {
        setErrorTexts((prev: IErrorTextExtraInfo) => ({
          ...prev,
          name: errors.length === 0 ? '' : 'errorName',
        }));
      }
      return errors.length === 0 ? '' : 'errorName';
    }
  }
};

/**
 * 약관 에러 텍스트 인터페이스
 * 약관 관련 에러 텍스트를 정의합니다.
 * @property {string} service - 서비스 약관 에러 텍스트
 * @property {string} privacy - 개인정보 약관 에러 텍스트
 * @property {string} location - 위치기반 약관 에러 텍스트
 */
export interface IErrorTextTerms {
  service: string; // 서비스 약관
  privacy: string; // 개인정보 약관
  location: string; // 위치기반 약관
}

/**
 * 약관 검증 인터페이스
 * 약관 검증에 필요한 파라미터를 정의합니다.
 * @property {IRegisterInfo} values - 회원가입 정보
 * @property {React.Dispatch<React.SetStateAction<IErrorTextTerms>>} setErrorTexts - 에러 텍스트 설정 함수
 */
export interface IValidateTerms {
  values: IRegisterInfo;
  setErrorTexts: React.Dispatch<React.SetStateAction<IErrorTextTerms>>;
}

/**
 * 이용 약관 동의 검증 함수
 * 이용 약관 동의 여부를 검증합니다.
 * @param {IValidateTerms} params - 약관 검증 파라미터
 * @returns {Promise<string>} 에러가 없으면 빈 문자열, 있으면 'signupErrorTxtTerm'
 */
export const validateTerms = async ({
  values,
  setErrorTexts,
}: IValidateTerms) => {
  const valueKeys = Object.keys(values);
  let errorMsg = '';

  for (const key of valueKeys) {
    if (key === 'service' || key === 'privacy' || key === 'location') {
      setErrorTexts((prev: IErrorTextTerms) => ({
        ...prev,
        [key]: values[key] ? '' : 'signupErrorTxtTerm',
      }));
      if (errorMsg === '') {
        errorMsg = values[key] ? '' : 'signupErrorTxtTerm';
      }
    }
  }
  return errorMsg;
};

/**
 * 로그인 검증 함수
 * 로그인 정보를 검증합니다.
 * @param {any} phoneNumber - 전화번호
 * @param {any} password - 비밀번호
 * @returns {Promise<any[]>} 검증 에러 배열
 */
export const isSignInValidate = async (phoneNumber: any, password: any) => {
  let testPhone = new siginDTO();
  testPhone.phone = phoneNumber;
  testPhone.password = password;
  const errors = await validate(testPhone);
  return errors;
};
